import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import { FiMail, FiLock, FiUser, FiEye, FiEyeOff, FiLoader } from 'react-icons/fi';
import { useAuth } from '../../context/AuthContext';
import { extractError } from '../../api/client';
import { toast } from '../../utils/alert';
import { COMPANY_NAME } from '../../config';

export default function Register() {
  const { register, isAuthenticated } = useAuth();
  const navigate = useNavigate();
  const [form, setForm] = useState({ name: '', email: '', password: '', password_confirmation: '' });
  const [showPass, setShowPass] = useState(false);
  const [loading, setLoading] = useState(false);
  const [errors, setErrors] = useState({});

  useEffect(() => {
    if (isAuthenticated) {
      navigate('/', { replace: true });
    }
  }, [isAuthenticated, navigate]);

  const handleChange = (e) => {
    setForm({ ...form, [e.target.name]: e.target.value });
    setErrors((prev) => ({ ...prev, [e.target.name]: '' }));
  };

  const handleSubmit = async (e) => {
    e.preventDefault();
    const errs = {};
    if (!form.name) errs.name = 'Name is required';
    if (!form.email) errs.email = 'Email is required';
    else if (!/^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(form.email)) errs.email = 'Enter a valid email address';
    if (form.password.length < 8) errs.password = 'Password must be at least 8 characters';
    if (form.password !== form.password_confirmation) errs.password_confirmation = 'Passwords do not match';
    if (Object.keys(errs).length) {
      setErrors(errs);
      return;
    }
    setLoading(true);
    try {
      await register(form);
      toast('success', '', 'Account created successfully!');
    } catch (error) {
      toast('error', '', extractError(error, 'Registration failed'));
    } finally {
      setLoading(false);
    }
  };

  return (
    <div className="auth-page">
      <div className="auth-left">
        <div className="auth-left-content">
          <div className="auth-brand">
            <img src="/logo.png" alt="Logo" className="logo-badge" style={{ width: 48, height: 48, borderRadius: 10, objectFit: 'contain' }} />
            <div>
              <div className="logo-name">{COMPANY_NAME}</div>
              <div className="logo-sub">Embroidery ERP</div>
            </div>
          </div>
          <div className="auth-headline">
            <h1>Start managing your business today.</h1>
            <p>
              Join the {COMPANY_NAME} ERP platform and get access to accounting,
              production, payroll and inventory tools.
            </p>
          </div>
        </div>
        <div className="auth-foot">© 2026 {COMPANY_NAME}. All rights reserved.</div>
      </div>

      <div className="auth-right">
        <div className="auth-card">
          <h2 className="auth-card-title">Create account</h2>
          <p className="auth-card-sub">Register to get started with {COMPANY_NAME} ERP.</p>

          <form onSubmit={handleSubmit} noValidate>
            <div className="form-group">
              <label className="form-label">Full Name</label>
              <div className="auth-input-wrap">
                <FiUser className="input-icon" />
                <input
                  className={`form-control ${errors.name ? 'is-invalid' : ''}`}
                  type="text"
                  name="name"
                  placeholder="e.g. Ali Khan"
                  value={form.name}
                  onChange={handleChange}
                />
              </div>
              {errors.name && <div className="invalid-feedback">{errors.name}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Email Address</label>
              <div className="auth-input-wrap">
                <FiMail className="input-icon" />
                <input
                  className={`form-control ${errors.email ? 'is-invalid' : ''}`}
                  type="email"
                  name="email"
                  placeholder="you@company.com"
                  value={form.email}
                  onChange={handleChange}
                />
              </div>
              {errors.email && <div className="invalid-feedback">{errors.email}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Password</label>
              <div className="auth-input-wrap">
                <FiLock className="input-icon" />
                <input
                  className={`form-control ${errors.password ? 'is-invalid' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  name="password"
                  placeholder="Min 8 characters"
                  value={form.password}
                  onChange={handleChange}
                />
                <button
                  type="button"
                  className="eye-toggle"
                  onClick={() => setShowPass((v) => !v)}
                  aria-label="Toggle password"
                >
                  {showPass ? <FiEyeOff /> : <FiEye />}
                </button>
              </div>
              {errors.password && <div className="invalid-feedback">{errors.password}</div>}
            </div>

            <div className="form-group">
              <label className="form-label">Confirm Password</label>
              <div className="auth-input-wrap">
                <FiLock className="input-icon" />
                <input
                  className={`form-control ${errors.password_confirmation ? 'is-invalid' : ''}`}
                  type={showPass ? 'text' : 'password'}
                  name="password_confirmation"
                  placeholder="Re-enter password"
                  value={form.password_confirmation}
                  onChange={handleChange}
                />
              </div>
              {errors.password_confirmation && <div className="invalid-feedback">{errors.password_confirmation}</div>}
            </div>

            <button type="submit" className="btn btn-primary btn-lg btn-block" disabled={loading}>
              {loading ? <><FiLoader /> Creating...</> : 'Create Account'}
            </button>
          </form>

          <div style={{ textAlign: 'center', fontSize: 13, color: 'var(--text-muted)', marginTop: 20 }}>
            Already have an account?{' '}
            <Link to="/login" className="auth-link">Sign in</Link>
          </div>
        </div>
      </div>
    </div>
  );
}